import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Table, Alert } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, updateDoc, doc, getDocs, query, where, onSnapshot, deleteDoc } from 'firebase/firestore';
import { storage, firestore } from '../firebase';
import IDCard from '../components/students/IDCard';
import RegistrationSlip from '../components/students/RegistrationSlip';
import EditStudentModal from '../components/students/EditStudentModal';
import FeeManagementModal from '../components/students/FeeManagementModal';
import BulkPrintModal from '../components/students/BulkPrintModal';
import PrintModal from '../components/students/PrintModal';
import ActionButtons from '../components/students/ActionButtons';
import ViewStudentModal from '../components/students/ViewStudentModal';
import CustomIDCard from '../components/students/CustomIDCard';
import CustomRegistrationSlip from '../components/students/CustomRegistrationSlip';
import { BsSearch, BsFilter, BsPerson, BsBook, BsGear, BsPrinter, BsCardHeading } from 'react-icons/bs';

const FALLBACK_PHOTO = '/no-photo.png';
const FALLBACK_LOGO = '/no-logo.png';

const StudentsManagement = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [showBulkPrint, setShowBulkPrint] = useState(false);
  const [showFeeManagement, setShowFeeManagement] = useState(false);
  const [feeDataMap, setFeeDataMap] = useState({});

  // Add missing states
  const [editMode, setEditMode] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printContent, setPrintContent] = useState(null);
  const [printTitle, setPrintTitle] = useState('');
  const [statsData, setStatsData] = useState({
    total: 0,
    oLevel: 0,
    adca: 0,
    dca: 0,
    other: 0,
    searchResults: 0
  });
  const [viewStudent, setViewStudent] = useState(null);

  // Refs
  const printRef = useRef(null);
  const componentRef = useRef(null);
  const modalRef = useRef(null);

  const schoolInfo = {
    name: 'GSK College',
    address: 'Hanumanganj Fatuhan Road 221505',
    logo: "/assets/logo.jpeg",
    phone: '+91 9876543210',
    email: 'aitraining2611@gmail.com',
    principal: 'Er.MANOJ KUMAR KUSHWAHA'
  };

  // Add the missing handlePrintDocument function
  const handlePrintDocument = (student, type) => {
    setSelectedStudent(student);
    setModalType(type);
  };

  // Add Stats component
  const DashboardStats = ({ loading, stats }) => {
    return (
      <div className="row g-3 mb-4">
        <div className="col-md">
          <div className="card bg-light border-0 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                <BsSearch className="text-primary fs-4" />
              </div>
              <div>
                <h6 className="mb-0 text-muted">Search Results</h6>
                <h3 className="mb-0">{loading ? "..." : stats.searchResults}</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md">
          <div className="card bg-light border-0 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                <BsPerson className="text-success fs-4" />
              </div>
              <div>
                <h6 className="mb-0 text-muted">Total Students</h6>
                <h3 className="mb-0">{stats.total}</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md">
          <div className="card bg-light border-0 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle bg-warning bg-opacity-10 p-3 me-3">
                <BsGear className="text-warning fs-4" />
              </div>
              <div>
                <h6 className="mb-0 text-muted">O Level Students</h6>
                <h3 className="mb-0">{stats.oLevel}</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md">
          <div className="card bg-light border-0 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                <BsPerson className="text-info fs-4" />
              </div>
              <div>
                <h6 className="mb-0 text-muted">ADCA Students</h6>
                <h3 className="mb-0">{stats.adca}</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md">
          <div className="card bg-light border-0 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle bg-secondary bg-opacity-10 p-3 me-3">
                <BsPerson className="text-secondary fs-4" />
              </div>
              <div>
                <h6 className="mb-0 text-muted">DCA Students</h6>
                <h3 className="mb-0">{stats.dca}</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md">
          <div className="card bg-light border-0 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle bg-danger bg-opacity-10 p-3 me-3">
                <BsBook className="text-danger fs-4" />
              </div>
              <div>
                <h6 className="mb-0 text-muted">Other Students</h6>
                <h3 className="mb-0">{stats.other}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const studentsRef = collection(firestore, 'admissions');
      const querySnapshot = await getDocs(studentsRef);
      
      const studentsData = [];
      const imagePromises = [];

      querySnapshot.forEach((doc) => {
        const student = doc.data();
        
        // Complete student data with all fields
        const completeStudent = {
          id: doc.id,
          studentName: student.studentName || 'N/A',
          rollNumber: student.rollNumber || student.registrationNumber || doc.id,
          grade: student.grade || student.class || 'N/A',
          section: student.section || 'N/A',
          course: student.course || student.courseName || 'N/A',
          fatherName: student.fatherName || student.father_name || 'N/A',
          motherName: student.motherName || student.mother_name || 'N/A',
          phone: student.phone || student.phoneNumber || 'N/A',
          address: student.address || 'N/A',
          email: student.email || 'N/A',
          dob: student.dob || student.dateOfBirth || 'N/A',
          gender: student.gender || 'N/A',
          photoURL: student.photoURL || FALLBACK_PHOTO,
          admissionDate: student.admissionDate || student.createdAt || 'N/A',
          // Add any other fields that might be in your database
          ...student
        };

        studentsData.push(completeStudent);

        // Firebase Storage से फोटो fetch करें - FIXED PHOTO URL HANDLING
        if (student.photoURL && !student.photoURL.startsWith('http')) {
          // If photoURL is not a full URL, construct the path
          const fileName = student.photoURL.replace(/^.*[\\\/]/, '');
          const imagePath = `admissions/${fileName}`;
          
          const imageRef = storageRef(storage, imagePath);
          const promise = getDownloadURL(imageRef)
            .then(url => ({ id: doc.id, url }))
            .catch((error) => {
              console.warn(`Failed to fetch image for student ${doc.id}:`, error);
              return { id: doc.id, url: FALLBACK_PHOTO };
            });
          imagePromises.push(promise);
        } else if (student.photoURL && student.photoURL.startsWith('http')) {
          // If it's already a full URL, use it directly
          const studentIndex = studentsData.findIndex(s => s.id === doc.id);
          if (studentIndex !== -1) {
            studentsData[studentIndex].photoURL = student.photoURL;
          }
        } else {
          // If no photoURL, push fallback immediately
          const studentIndex = studentsData.findIndex(s => s.id === doc.id);
          if (studentIndex !== -1) {
            studentsData[studentIndex].photoURL = FALLBACK_PHOTO;
          }
        }
      });

      // Process all image promises
      if (imagePromises.length > 0) {
        const imageResults = await Promise.allSettled(imagePromises);
        imageResults.forEach(result => {
          if (result.status === 'fulfilled') {
            const studentIndex = studentsData.findIndex(s => s.id === result.value.id);
            if (studentIndex !== -1) {
              studentsData[studentIndex].photoURL = result.value.url;
            }
          } else {
            console.warn('Image promise rejected:', result.reason);
          }
        });
      }

      setStudents(studentsData);
      
      // Calculate course-wise statistics
      const calculateStats = (studentsList) => {
        const oLevel = studentsList.filter(s => {
          const course = s.course ? s.course.toLowerCase() : '';
          return course.includes('o level') || 
                 course.includes('olevel') ||
                 course.includes('o-level');
        }).length;

        const adca = studentsList.filter(s => {
          const course = s.course ? s.course.toLowerCase() : '';
          return course.includes('adca');
        }).length;

        const dca = studentsList.filter(s => {
          const course = s.course ? s.course.toLowerCase() : '';
          return course.includes('dca');
        }).length;

        // Other courses (excluding primary and CCA)
        const other = studentsList.filter(s => {
          const course = s.course ? s.course.toLowerCase() : '';
          return !course.includes('primary') && 
                 !course.includes('o level') && 
                 !course.includes('olevel') &&
                 !course.includes('o-level') &&
                 !course.includes('adca') &&
                 !course.includes('dca') &&
                 !course.includes('cca');
        }).length;

        return {
          total: studentsList.length,
          oLevel,
          adca,
          dca,
          other,
          searchResults: studentsList.length
        };
      };

      const stats = calculateStats(studentsData);
      setStatsData(stats);
      await fetchAllFeeData(studentsData);
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Failed to load students. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllFeeData = async (studentsList) => {
    try {
      const feeMap = {};
      
      // Fetch fee data for each student
      for (const student of studentsList) {
        const q = query(
          collection(firestore, 'fees'),
          where('studentId', '==', student.id)
        );
        
        const querySnapshot = await getDocs(q);
        const fees = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fees.push({ 
            id: doc.id, 
            ...data,
            // Convert Firestore timestamps to Date objects
            dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : data.dueDate,
            paymentDate: data.paymentDate?.toDate ? data.paymentDate.toDate() : data.paymentDate
          });
        });
        
        feeMap[student.id] = fees;
      }
      
      setFeeDataMap(feeMap);
    } catch (error) {
      console.error('Error fetching fee data:', error);
    }
  };

  useEffect(() => {
    fetchStudents();
    
    // Set up real-time listener for fee updates
    const unsubscribe = onSnapshot(collection(firestore, 'fees'), () => {
      // Refresh fee data when changes occur
      if (students.length > 0) {
        fetchAllFeeData(students);
      }
    });
    
    return () => unsubscribe();
  }, []);

  const filteredStudents = students.filter(student => {
    const searchTerm = search.toLowerCase();
    return (
      (student.studentName && student.studentName.toLowerCase().includes(searchTerm)) ||
      (student.rollNumber && student.rollNumber.toLowerCase().includes(searchTerm)) ||
      (student.course && student.course.toLowerCase().includes(searchTerm)) ||
      (student.fatherName && student.fatherName.toLowerCase().includes(searchTerm)) ||
      (student.id && student.id.toLowerCase().includes(searchTerm))
    );
  });

  // Update stats when search changes
  useEffect(() => {
    const calculateStats = (studentsList) => {
      const oLevel = studentsList.filter(s => {
        const course = s.course ? s.course.toLowerCase() : '';
        return course.includes('o level') || 
               course.includes('olevel') ||
               course.includes('o-level');
      }).length;

      const adca = studentsList.filter(s => {
        const course = s.course ? s.course.toLowerCase() : '';
        return course.includes('adca');
      }).length;

      const dca = studentsList.filter(s => {
        const course = s.course ? s.course.toLowerCase() : '';
        return course.includes('dca');
      }).length;

      // Other courses (excluding primary and CCA)
      const other = studentsList.filter(s => {
        const course = s.course ? s.course.toLowerCase() : '';
        return !course.includes('primary') && 
               !course.includes('o level') && 
               !course.includes('olevel') &&
               !course.includes('o-level') &&
               !course.includes('adca') &&
               !course.includes('dca') &&
               !course.includes('cca');
      }).length;

      return {
        total: students.length,
        oLevel,
        adca,
        dca,
        other,
        searchResults: studentsList.length
      };
    };

    const searchStats = calculateStats(filteredStudents);
    setStatsData(searchStats);
  }, [filteredStudents, students]);

  const openModal = (student, type) => {
    setSelectedStudent(student);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedStudent(null);
    setModalType(null);
  };

  const openFeeManagement = (student) => {
    setSelectedStudent(student);
    setShowFeeManagement(true);
  };

  const closeFeeManagement = () => {
    setSelectedStudent(null);
    setShowFeeManagement(false);
  };

  const handleFeeUpdate = () => {
    // Refresh fee data when a fee is updated
    if (students.length > 0) {
      fetchAllFeeData(students);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .card {
          break-inside: avoid;
        }
      }
    `,
    onBeforeGetContent: () => {
      // Any preparation before printing
    },
    onAfterPrint: () => {
      // Any cleanup after printing
      closeModal();
    }
  });

  const getDocumentComponent = () => {
    if (!selectedStudent) return null;

    const commonProps = {
      student: selectedStudent,
      schoolInfo,
      feeData: feeDataMap[selectedStudent.id] || []
    };

    switch (modalType) {
      case 'idCard':
        return <IDCard {...commonProps} ref={printRef} />;
      case 'registrationSlip':
        return <RegistrationSlip {...commonProps} ref={printRef} />;
      default:
        return null;
    }
  };

  const handleEdit = async (formData) => {
    if (!editStudent) return;
    
    try {
      const docRef = doc(firestore, 'admissions', editStudent.id);
      await updateDoc(docRef, {
        ...formData,
        updatedAt: new Date()
      });
      
      await fetchStudents(); // Refresh the list
      setEditMode(false);
      setEditStudent(null);
      alert('Student updated successfully!');
    } catch (error) {
      console.error('Error updating student:', error);
      alert('Failed to update student');
    }
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    
    try {
      const docRef = doc(firestore, 'admissions', studentId);
      await deleteDoc(docRef);
      await fetchStudents(); // Refresh the list
      alert('Student deleted successfully!');
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('Failed to delete student');
    }
  };

  const sendFeeReminder = async (student) => {
    try {
      const fees = feeDataMap[student.id] || [];
      const pendingFees = fees.filter(fee => fee.status === 'Pending');
      
      if (pendingFees.length === 0) {
        alert('No pending fees for this student');
        return;
      }

      const totalPending = pendingFees.reduce((sum, fee) => sum + fee.amount, 0);
      const message = `
        Dear ${student.studentName},
        You have pending fees of ₹${totalPending}.
        Please clear your dues at your earliest convenience.
        
        Details:
        ${pendingFees.map(fee => `
          - ${fee.feeType}: ₹${fee.amount}
          Due Date: ${new Date(fee.dueDate).toLocaleDateString()}
        `).join('\n')}
        
        Regards,
        School Administration
      `;

      // Implement your SMS/Email service here
      // For example:
      // await sendSMS(student.phone, message);
      // await sendEmail(student.email, 'Fee Reminder', message);
      
      alert('Reminder sent successfully!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder');
    }
  };

  const handleBulkPrint = (type) => {
    if (!filteredStudents.length) {
      alert('No students selected for printing');
      return;
    }

    const content = (
      <div>
        {filteredStudents.map((student, index) => (
          <React.Fragment key={student.id}>
            {type === 'idCard' ? (
              <IDCard 
                student={student} 
                schoolInfo={schoolInfo} 
              />
            ) : (
              <RegistrationSlip 
                student={student} 
                schoolInfo={schoolInfo}
                feeData={feeDataMap[student.id] || []}
              />
            )}
            {index < filteredStudents.length - 1 && (
              <div style={{ pageBreakAfter: 'always' }} />
            )}
          </React.Fragment>
        ))}
      </div>
    );

    setPrintContent(content);
    setPrintTitle(type === 'idCard' ? 'Student ID Cards' : 'Registration Slips');
    setShowPrintModal(true);
  };

  // Add handleView function
  const handleView = (student) => {
    setViewStudent(student);
  };

  return (
    <div className="container-fluid p-4">
      <DashboardStats loading={loading} stats={statsData} />
      
      {/* Search & Filter Section */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text border-0 bg-light">
                  <BsSearch />
                </span>
                <input
                  type="text"
                  className="form-control border-0 bg-light"
                  placeholder="Search student by ID/Name/Course/Father Name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-md-3">
              <select className="form-select border-0 bg-light">
                <option>All Courses</option>
                <option>O Level</option>
                <option>ADCA</option>
                <option>DCA</option>
                <option>Other</option>
              </select>
            </div>

            <div className="col-md-3">
              <select className="form-select border-0 bg-light">
                <option>All Grades</option>
                <option>Grade 1</option>
                <option>Grade 2</option>
                <option>Grade 3</option>
                <option>Grade 4</option>
                <option>Grade 5</option>
              </select>
            </div>

            <div className="col-md-2 text-end">
              <button className="btn btn-primary" onClick={() => setShowPrintModal(true)}>
                <i className="bi bi-plus-lg me-2"></i>
                Add Student
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading students...</p>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No students found.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>
                      <input type="checkbox" className="form-check-input" />
                    </th>
                    <th>Photo</th>
                    <th>Full Name</th>
                    <th>Reg Num</th>
                    <th>Course</th>
                    <th>Grade</th>
                    <th>Father Name</th>
                    <th>Phone</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map(student => (
                    <tr key={student.id}>
                      <td>
                        <input type="checkbox" className="form-check-input" />
                      </td>
                      <td>
                        <img
                          src={student.photoURL}
                          alt={student.studentName}
                          className="rounded-circle"
                          style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                          onError={(e) => { 
                            e.target.src = FALLBACK_PHOTO;
                            console.warn(`Failed to load image for ${student.studentName}: ${student.photoURL}`);
                          }}
                        />
                      </td>
                      <td>
                        <div className="fw-semibold">{student.studentName}</div>
                        <small className="text-muted">{student.gender}</small>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{student.rollNumber}</span>
                      </td>
                      <td>
                        <span className="badge bg-primary">{student.course}</span>
                      </td>
                      <td>{student.grade}</td>
                      <td>{student.fatherName}</td>
                      <td>{student.phone}</td>
                      <td>
                        <ActionButtons 
                          student={student}
                          onView={handleView}
                          onEdit={setEditStudent}
                          onDelete={handleDelete}
                          onReminder={sendFeeReminder}
                          onFeeManagement={openFeeManagement}
                          onPrintCard={() => handlePrintDocument(student, 'idCard')}
                          onPrintSlip={() => handlePrintDocument(student, 'registrationSlip')}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add ViewStudentModal */}
      <ViewStudentModal
        show={!!viewStudent}
        onHide={() => setViewStudent(null)}
        student={viewStudent}
        onEdit={setEditStudent}
        onPrintCard={(student) => handlePrintDocument(student, 'idCard')}
        onPrintSlip={(student) => handlePrintDocument(student, 'registrationSlip')}
      />

      {/* Edit Student Modal */}
      <EditStudentModal
        show={!!editStudent}
        onHide={() => setEditStudent(null)}
        student={editStudent}
        onUpdate={handleEdit}
      />

      <Modal show={!!modalType} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'idCard' && 'Student ID Card'}
            {modalType === 'registrationSlip' && 'Student Registration Slip'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center">
          <div ref={printRef}>
            {getDocumentComponent()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Close</Button>
          <Button variant="primary" onClick={handlePrint}>
            <i className="bi bi-printer-fill me-2"></i>
            Print
          </Button>
        </Modal.Footer>
      </Modal>

      <FeeManagementModal
        show={showFeeManagement}
        onHide={closeFeeManagement}
        student={selectedStudent}
        onFeeUpdate={handleFeeUpdate}
      />

      <BulkPrintModal
        show={showBulkPrint}
        onHide={() => setShowBulkPrint(false)}
        students={filteredStudents}
        schoolInfo={schoolInfo}
        feeDataMap={feeDataMap}
      />

      <PrintModal
        ref={modalRef}
        show={showPrintModal}
        onHide={() => setShowPrintModal(false)}
        content={printContent}
        title={printTitle}
      />
    </div>
  );
};

export default StudentsManagement;